/**
 * Theme Picker Component
 *
 * Visual theme selector with preview cards.
 * Features:
 * - Pre-built theme presets
 * - Custom theme creation
 * - Theme import/export
 * - Visual preview cards
 */

import { useState, useEffect } from 'react';
import {
  Check,
  Palette,
  Plus,
  Download,
  Upload,
  Trash2,
  Save,
  Sparkles,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import {
  type PageTheme,
  getAllThemes,
  saveCustomTheme,
  deleteCustomTheme,
  exportTheme,
  importTheme,
} from '@/client/lib/themes';

interface ThemePickerProps {
  /** Currently applied theme settings */
  currentTheme: {
    backgroundColor: string;
    accentColor: string;
    textColor: string;
    headingColor: string;
    fontFamily:
      | 'inter'
      | 'poppins'
      | 'merriweather'
      | 'system'
      | 'playfair'
      | 'roboto'
      | 'montserrat'
      | 'lora';
    maxWidth: 'narrow' | 'medium' | 'wide';
    padding: 'compact' | 'normal' | 'spacious';
  };
  /** Callback when theme is applied */
  onApplyTheme: (theme: PageTheme) => void;
}

type TabType = 'preset' | 'custom';

/**
 * Theme Preview Card
 */
function ThemeCard({
  theme,
  isActive,
  onApply,
  onDelete,
}: {
  theme: PageTheme;
  isActive: boolean;
  onApply: () => void;
  onDelete?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onApply}
      className={cn(
        'group relative rounded-xl border-2 overflow-hidden transition-all text-left',
        'hover:shadow-lg hover:scale-[1.02]',
        isActive
          ? 'border-primary ring-2 ring-primary ring-offset-2'
          : 'border-border hover:border-primary/50'
      )}
    >
      {/* Preview Header */}
      <div
        className={cn(
          'h-24 p-4 flex items-center justify-center relative overflow-hidden',
          `bg-gradient-to-br ${theme.preview?.gradient || 'from-gray-500 to-gray-600'}`
        )}
      >
        {/* Icon/Emoji */}
        <div className="text-4xl drop-shadow-lg">{theme.preview?.icon || '🎨'}</div>

        {/* Active Checkmark */}
        {isActive && (
          <div className="absolute top-2 right-2 bg-white dark:bg-gray-900 rounded-full p-1">
            <Check className="h-4 w-4 text-primary" />
          </div>
        )}

        {/* Delete button for custom themes */}
        {theme.category === 'custom' && onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute top-2 left-2 bg-destructive/90 hover:bg-destructive text-white rounded-lg p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Theme Info */}
      <div className="p-3 bg-background">
        <h3 className="font-semibold text-sm mb-1 truncate">{theme.name}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{theme.description}</p>

        {/* Color Dots */}
        <div className="flex gap-1.5">
          <div
            className="w-5 h-5 rounded-full border border-border"
            style={{ backgroundColor: theme.colors.backgroundColor }}
            title="Background"
          />
          <div
            className="w-5 h-5 rounded-full border border-border"
            style={{ backgroundColor: theme.colors.accentColor }}
            title="Accent"
          />
          <div
            className="w-5 h-5 rounded-full border border-border"
            style={{ backgroundColor: theme.colors.textColor }}
            title="Text"
          />
        </div>
      </div>
    </button>
  );
}

/**
 * Main Theme Picker Component
 */
export function ThemePicker({ currentTheme, onApplyTheme }: ThemePickerProps) {
  const [activeTab, setActiveTab] = useState<TabType>('preset');
  const [themes, setThemes] = useState<PageTheme[]>([]);
  const [customThemeName, setCustomThemeName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Load themes on mount and when tab changes
  useEffect(() => {
    setThemes(getAllThemes());
  }, [activeTab]);

  const handleApplyTheme = (theme: PageTheme) => {
    onApplyTheme(theme);
    toast.success(`Applied "${theme.name}" theme`);
  };

  const handleSaveCustomTheme = () => {
    if (!customThemeName.trim()) {
      toast.error('Please enter a theme name');
      return;
    }

    const newTheme = saveCustomTheme({
      name: customThemeName,
      description: 'Custom theme created from current settings',
      colors: {
        backgroundColor: currentTheme.backgroundColor,
        accentColor: currentTheme.accentColor,
        textColor: currentTheme.textColor,
        headingColor: currentTheme.headingColor,
      },
      typography: {
        fontFamily: currentTheme.fontFamily,
      },
      layout: {
        maxWidth: currentTheme.maxWidth,
        padding: currentTheme.padding,
      },
      preview: {
        gradient: 'from-violet-500 to-purple-600',
        icon: '🎨',
      },
    });

    setThemes(getAllThemes());
    setCustomThemeName('');
    setShowSaveDialog(false);
    setActiveTab('custom');
    toast.success(`Saved "${newTheme.name}" theme`);
  };

  const handleDeleteTheme = (themeId: string) => {
    deleteCustomTheme(themeId);
    setThemes(getAllThemes());
    toast.success('Theme deleted');
  };

  const handleExportTheme = (theme: PageTheme) => {
    const json = exportTheme(theme);
    navigator.clipboard.writeText(json);
    toast.success('Theme JSON copied to clipboard');
  };

  const handleImportTheme = () => {
    navigator.clipboard.readText().then((text) => {
      const imported = importTheme(text);
      if (imported) {
        setThemes(getAllThemes());
        setActiveTab('custom');
        toast.success(`Imported "${imported.name}" theme`);
      } else {
        toast.error('Invalid theme JSON');
      }
    });
  };

  const presetThemes = themes.filter((t) => t.category === 'preset');
  const customThemes = themes.filter((t) => t.category === 'custom');

  // Check if current settings match any theme
  const activeTheme = themes.find(
    (t) =>
      t.colors.backgroundColor === currentTheme.backgroundColor &&
      t.colors.accentColor === currentTheme.accentColor &&
      t.colors.textColor === currentTheme.textColor &&
      t.typography.fontFamily === currentTheme.fontFamily
  );

  return (
    <div className="space-y-4">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
          <Button
            type="button"
            variant={activeTab === 'preset' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('preset')}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Presets
          </Button>
          <Button
            type="button"
            variant={activeTab === 'custom' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('custom')}
            className="gap-2"
          >
            <Users className="h-4 w-4" />
            Custom ({customThemes.length})
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowSaveDialog(!showSaveDialog)}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save Current
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleImportTheme}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Import
          </Button>
        </div>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="p-4 border rounded-xl bg-muted/20 space-y-3">
          <div className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold">Save Current Theme</h3>
          </div>
          <Input
            type="text"
            placeholder="My Custom Theme"
            value={customThemeName}
            onChange={(e) => setCustomThemeName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveCustomTheme();
            }}
          />
          <div className="flex gap-2">
            <Button type="button" onClick={handleSaveCustomTheme} className="gap-2">
              <Plus className="h-4 w-4" />
              Save Theme
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowSaveDialog(false);
                setCustomThemeName('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Theme Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {activeTab === 'preset' ? (
          presetThemes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isActive={activeTheme?.id === theme.id}
              onApply={() => handleApplyTheme(theme)}
            />
          ))
        ) : customThemes.length > 0 ? (
          customThemes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isActive={activeTheme?.id === theme.id}
              onApply={() => handleApplyTheme(theme)}
              onDelete={() => handleDeleteTheme(theme.id)}
            />
          ))
        ) : (
          <div className="col-span-full p-8 border-2 border-dashed rounded-xl text-center">
            <Palette className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold mb-1">No Custom Themes Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Save your current settings as a custom theme to reuse later
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSaveDialog(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Save Current Theme
            </Button>
          </div>
        )}
      </div>

      {/* Export Button (shown when hovering theme in custom tab) */}
      {activeTab === 'custom' && activeTheme && activeTheme.category === 'custom' && (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleExportTheme(activeTheme)}
            className="gap-2 text-xs"
          >
            <Download className="h-3 w-3" />
            Export &quot;{activeTheme.name}&quot; as JSON
          </Button>
        </div>
      )}
    </div>
  );
}
